<template>
  <v-container>
    <v-card class="mx-auto white" max-width="800">
      <v-card-title class="text-h5">
        Profile Settings
      </v-card-title>
      
      <v-card-text>
        <v-form ref="form" v-model="valid">
          <!-- Personal Information Section -->
          <v-subheader class="pl-0">Personal Information</v-subheader>
          <v-text-field
            v-model="profile.name"
            label="Full Name"
            :rules="nameRules"
            required
          ></v-text-field>

          <v-text-field
            v-model="profile.email"
            label="Email"
            :rules="emailRules"
            required
            type="email"
          ></v-text-field>

          <!-- Password Change Section -->
          <v-divider class="my-5"></v-divider>
          <v-subheader class="pl-0">Change Password</v-subheader>
          
          <v-text-field
            v-model="passwords.current"
            label="Current Password"
            :type="showCurrentPassword ? 'text' : 'password'"
            :append-icon="showCurrentPassword ? 'mdi-eye' : 'mdi-eye-off'"
            @click:append="showCurrentPassword = !showCurrentPassword"
            :rules="passwordRules"
          ></v-text-field>

          <v-text-field
            v-model="passwords.new"
            label="New Password"
            :type="showNewPassword ? 'text' : 'password'"
            :append-icon="showNewPassword ? 'mdi-eye' : 'mdi-eye-off'"
            @click:append="showNewPassword = !showNewPassword"
            :rules="passwordRules"
          ></v-text-field>

          <v-text-field
            v-model="passwords.confirm"
            label="Confirm New Password"
            :type="showConfirmPassword ? 'text' : 'password'"
            :append-icon="showConfirmPassword ? 'mdi-eye' : 'mdi-eye-off'"
            @click:append="showConfirmPassword = !showConfirmPassword"
            :rules="[...passwordRules, passwordMatch]"
          ></v-text-field>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          :disabled="!valid"
          @click="saveChanges"
        >
          Save Changes
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Confirmation Dialog -->
    <v-dialog v-model="dialog" max-width="400">
      <v-card class="white">
        <v-card-title class="text-h5">
          Confirm Changes
        </v-card-title>
        <v-card-text>
          Are you sure you want to update your profile information?
          <template v-if="passwords.new">
            This will also change your password.
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="grey darken-1"
            text
            @click="dialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            @click="confirmChanges"
          >
            Confirm
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Success/Error Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      timeout="3000"
    >
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="snackbar.show = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import axios from 'axios'
import TokenService from '@/services/storage.service'

export default {
  name: 'Profile',
  data: () => ({
    valid: false,
    dialog: false,
    profile: {
      name: '',
      email: '',
    },
    passwords: {
      current: '',
      new: '',
      confirm: '',
    },
    showCurrentPassword: false,
    showNewPassword: false,
    showConfirmPassword: false,
    snackbar: {
      show: false,
      text: '',
      color: 'success',
    },
    nameRules: [
      v => !!v || 'Name is required',
      v => v.length >= 3 || 'Name must be at least 3 characters',
    ],
    emailRules: [
      v => !!v || 'Email is required',
      v => /.+@.+\..+/.test(v) || 'Email must be valid',
    ],
    passwordRules: [
      v => !v || v.length >= 8 || 'Password must be at least 8 characters',
      v => !v || /\d/.test(v) || 'Password must contain at least one number',
      v => !v || /[A-Z]/.test(v) || 'Password must contain at least one uppercase letter',
    ],
  }),
  computed: {
    ...mapGetters({
      authenticated: 'auth/authenticated'
    }),
    passwordMatch() {
      return () => this.passwords.new === this.passwords.confirm || 'Passwords do not match'
    },
  },
  methods: {
    async makeAuthenticatedRequest(method, url, data = null) {
      const token = TokenService.getToken()
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
      
      if (method.toLowerCase() === 'get') {
        return await axios.get(url, config)
      } else if (method.toLowerCase() === 'put') {
        return await axios.put(url, data, config)
      }
    },
    
    async saveChanges() {
      if (this.$refs.form.validate()) {
        // Additional validation for password change
        if (this.passwords.new || this.passwords.current || this.passwords.confirm) {
          // If any password field is filled, all must be filled
          if (!this.passwords.current || !this.passwords.new || !this.passwords.confirm) {
            this.snackbar.color = 'error'
            this.snackbar.text = 'All password fields are required for password change'
            this.snackbar.show = true
            return
          }
        }
        
        // Check if anything has actually changed
        if (this.profile.name === this.authenticated.name && 
            this.profile.email === this.authenticated.email && 
            !this.passwords.new) {
          this.snackbar.color = 'warning'
          this.snackbar.text = 'No changes to save'
          this.snackbar.show = true
          return
        }

        this.dialog = true
      }
    },
    async confirmChanges() {
      try {
        const profileData = {
          name: this.profile.name,
          email: this.profile.email
        }

        if (this.passwords.new) {
          if (!this.passwords.current || !this.passwords.new || !this.passwords.confirm) {
            this.snackbar.color = 'error'
            this.snackbar.text = 'All password fields are required for password change'
            this.snackbar.show = true
            this.dialog = false
            return
          }

          const passwordData = {
            current_password: this.passwords.current,
            new_password: this.passwords.new,
            new_password_confirmation: this.passwords.confirm
          }

          try {
            // First update the profile
            await this.makeAuthenticatedRequest('put', '/profile', profileData)
            
            // Then update the password
            await this.makeAuthenticatedRequest('put', '/change-password', passwordData)

            this.snackbar.color = 'success'
            this.snackbar.text = 'Profile and password updated successfully'
          } catch (error) {
            throw {
              message: error.response?.data?.message || 'Failed to update password',
              type: 'password'
            }
          }
        } else {
          // Only update profile information
          await this.makeAuthenticatedRequest('put', '/profile', profileData)
          this.snackbar.color = 'success'
          this.snackbar.text = 'Profile updated successfully'
        }

        // Reset password fields
        this.passwords.current = ''
        this.passwords.new = ''
        this.passwords.confirm = ''
        
        // Close dialog
        this.dialog = false
        this.snackbar.show = true

        // Update Vuex state with new user data
        await this.$store.dispatch('auth/getUser')
      } catch (error) {
        console.error('Update error:', error)
        this.snackbar.color = 'error'
        this.snackbar.text = error.response?.data?.message || error.message || 'An error occurred while updating profile'
        this.snackbar.show = true
        this.dialog = false
      }
    },
  },
  created() {
    // Set initial values from Vuex authenticated user data
    this.profile.name = this.authenticated.name
    this.profile.email = this.authenticated.email
  },
  watch: {
    // Watch for changes in authenticated user data
    authenticated: {
      handler(newValue) {
        this.profile.name = newValue.name
        this.profile.email = newValue.email
      },
      deep: true
    }
  }
}
</script> 